/**
 * 純碁 盤 (9x9 = 9 路) コンポーネント。 SVG で格子線を引き、
 * 各交点に absolute 配置のボタンを置く Renju パターン。
 *
 * 純碁特有の追加表示:
 * - 確定石 (= pass-alive) の中央に小さなマーカー (黒石=淡金、 白石=濃グレー)
 * - 上端のスコアバー: 「黒 N (確定 P / 42)  白 M+6.5 (確定 P / 36)」
 *   分裂 (G > 1) 時は閾値を bonus 分減じて表示 (例: `/ 41`)
 */

import type { ReactNode, RefObject } from 'react';
import { useRef } from 'react';
import {
  isPlace,
  JUNGO_BLACK_MKO_BASELINE,
  JUNGO_KOMI,
  JUNGO_SIZE,
  JUNGO_WHITE_MKO_BASELINE,
  type JungoMove,
  type JungoState,
  jungoBlackMkoThreshold,
  jungoWhiteMkoThreshold,
  legalKey,
  type PassAliveInfo,
  passAliveSet,
  type Side,
} from '../../games/jungo';
import { usePendingConfirm } from '../../hooks/usePendingConfirm';
import { useFirstPaint, useFreshLastMove } from '../../lib/first-paint';
import { BoardCoords } from './BoardCoords';

interface Props {
  state: JungoState;
  legal?: JungoMove[];
  passAlive?: PassAliveInfo;
  yourSide?: Side | null;
  myTurnAllowed?: boolean;
  onMove?: (mv: JungoMove) => void;
  mini?: boolean;
  lastMove?: JungoMove;
  /** Replay / Archive 等の静的ビューでは「相手の手番」 表示を出さない。 */
  staticView?: boolean;
  hideOwnSide?: boolean;
  tutorialHighlight?: [number, number][];
  /** 詰めパズルのように「陣地の数え」 が要らない場面でスコアバーと自分の色を隠す。 */
  hideScore?: boolean;
}

/** 9 路の星 (3 つ): 隅 (2,2)/(2,6)/(6,2)/(6,6) と天元 (4,4)。 */
const STAR_POINTS: [number, number][] = [
  [2, 2],
  [2, 6],
  [6, 2],
  [6, 6],
  [4, 4],
];

/** Go の列ラベル (I を除く): A, B, C, D, E, F, G, H, J, K, L, M, N, O, P, Q, R, S, T */
const GO_FILE_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T'];

export function JungoBoard({
  state,
  legal,
  passAlive,
  yourSide,
  myTurnAllowed,
  onMove,
  mini,
  lastMove,
  staticView,
  hideOwnSide,
  tutorialHighlight,
  hideScore,
}: Props) {
  // hooks は early return の前に必ず呼ぶ (React rules of hooks)。
  const { pending: pendingTap, tap: handleTap } = usePendingConfirm<string>(state);
  // 盤ごと mount した最初の描画では、 石を置くアニメも直前手のリングも出さない
  // (lib/first-paint.ts)。 以降に置かれた石だけがアニメする。
  const firstPaint = useFirstPaint();
  const freshLast = useFreshLastMove(lastMove);

  // WASM 未 init や server snapshot 直前の race で state.board が空のことがある。
  if (!state || !Array.isArray(state.board)) {
    return <div className="jungo-frame" />;
  }
  // pass-alive は state.pass_alive がデフォルト (engine 側でキャッシュ済み)。
  // caller が override したい場合のみ passAlive prop で差し替え可能。
  const pa = passAlive ?? state.pass_alive;
  const N = JUNGO_SIZE;
  const isMyTurn =
    myTurnAllowed !== undefined
      ? myTurnAllowed
      : yourSide != null && yourSide === state.side;
  const legalSet = new Set(
    (legal ?? []).filter(isPlace).map((m) => legalKey(m.Place.row, m.Place.col)),
  );
  const hasPass = (legal ?? []).some((m) => m === 'Pass');
  const lastKey =
    lastMove && isPlace(lastMove)
      ? legalKey(lastMove.Place.row, lastMove.Place.col)
      : null;
  const sideClass = state.side === 'First' ? 'side-black' : 'side-white';
  // Jungo は state に outcome を持たない (passes>=2 で終局判定)。 staticView を立てれば抑制。
  const showOppHint = !mini && !staticView && !isMyTurn;
  const pendingColor: 'First' | 'Second' = (yourSide ?? state.side) as 'First' | 'Second';

  const blackPaSet = pa ? passAliveSet(pa.black_pa_cells) : new Set<string>();
  const whitePaSet = pa ? passAliveSet(pa.white_pa_cells) : new Set<string>();

  const blackStones = state.board.flat().filter((c) => c === 'Black').length;
  const whiteStones = state.board.flat().filter((c) => c === 'White').length;
  const blackPa = pa?.black_pa_stones ?? 0;
  const whitePa = pa?.white_pa_stones ?? 0;
  const blackSystems = pa?.black_systems ?? 0;
  const whiteSystems = pa?.white_systems ?? 0;
  const blackBonus = blackSystems > 1 ? blackSystems - 1 : 0;
  const whiteBonus = whiteSystems > 1 ? whiteSystems - 1 : 0;

  const blackNear = jungoBlackMkoThreshold(blackSystems) - blackPa <= 3;
  const whiteNear = jungoWhiteMkoThreshold(whiteSystems) - whitePa <= 3;

  const pctOf = (i: number) => (i / (N - 1)) * 100;

  // 自分の色を常時表示 (連珠の RenjuBoard と同じ規約。 囲碁にはスワップが無いので yourSide そのまま)。
  const yourColorLabel =
    yourSide === 'First' ? '黒 (先手)' : yourSide === 'Second' ? '白 (後手)' : null;
  const yourColorClass =
    yourSide === 'First' ? 'is-black' : yourSide === 'Second' ? 'is-white' : '';

  return (
    <div className={`jungo-frame ${mini ? 'mini' : ''}`}>
      {!mini && !hideOwnSide && !hideScore && yourColorLabel && (
        <div className={`jungo-side-indicator ${yourColorClass}`} data-testid="jungo-your-color">
          <span className="jungo-side-stone" aria-hidden />
          あなた: {yourColorLabel}
        </div>
      )}
      {!mini && !hideScore && (
        <div className="jungo-scorebar" data-testid="jungo-scorebar">
          <span className={`jungo-score black ${blackNear ? 'near' : ''}`}>
            黒 <strong>{blackStones}</strong> (確定 {blackPa} /{' '}
            {JUNGO_BLACK_MKO_BASELINE - blackBonus})
          </span>
          <span className={`jungo-score white ${whiteNear ? 'near' : ''}`}>
            白 <strong>{whiteStones}</strong>+{JUNGO_KOMI} (確定 {whitePa} /{' '}
            {JUNGO_WHITE_MKO_BASELINE - whiteBonus})
          </span>
        </div>
      )}
      <div
        className={`jungo-board ${mini ? 'mini' : ''} ${sideClass} ${
          showOppHint ? 'not-my-turn' : ''
        }`}
      >
        <div className="jungo-grid-area">
          {/* 座標は盤線の外 (木地) に。 交点の中に置くと石で隠れる。 */}
          {!mini && (
            <BoardCoords
              variant="intersection"
              files={Array.from({ length: N }, (_, c) => GO_FILE_LETTERS[c] ?? '')}
              ranks={Array.from({ length: N }, (_, r) => `${N - r}`)}
            />
          )}
          <svg
            className="jungo-grid-svg"
            viewBox={`0 0 ${N - 1} ${N - 1}`}
            preserveAspectRatio="none"
            aria-hidden
          >
            {Array.from({ length: N }, (_, i) => (
              <g key={`l${i}`}>
                <line x1={0} y1={i} x2={N - 1} y2={i} stroke="#1a1a1a" strokeWidth="0.04" />
                <line x1={i} y1={0} x2={i} y2={N - 1} stroke="#1a1a1a" strokeWidth="0.04" />
              </g>
            ))}
            {STAR_POINTS.map(([r, c]) => (
              <circle key={`s${r}-${c}`} cx={c} cy={r} r={0.16} fill="#1a1a1a" />
            ))}
          </svg>

          {state.board.flatMap((row, r) =>
            row.map((cell, c) => {
              const k = legalKey(r, c);
              const isLegal = isMyTurn && legalSet.has(k);
              const isLast = !mini && lastKey === k;
              const isPendingTap = !mini && pendingTap === k;
              const isTutorialHint =
                tutorialHighlight?.some(([hr, hc]) => hr === r && hc === c) ?? false;
              const isPa =
                (cell === 'Black' && blackPaSet.has(k)) ||
                (cell === 'White' && whitePaSet.has(k));
              const handler = () => {
                handleTap(k, isLegal, () => onMove?.({ Place: { row: r, col: c } }));
              };
              const stoneClass =
                cell === 'Black' ? 'black' : cell === 'White' ? 'white' : '';
              const pendingTapClass = isPendingTap
                ? pendingColor === 'First' ? 'pending-tap-black' : 'pending-tap-white'
                : '';
              const cls = [
                'jungo-intersection',
                stoneClass,
                isLegal ? 'legal' : '',
                isLast ? 'last-move' : '',
                isLast && !freshLast ? 'no-anim' : '',
                pendingTapClass,
                isTutorialHint ? 'tutorial-hint' : '',
              ]
                .filter(Boolean)
                .join(' ');
              const style = { left: `${pctOf(c)}%`, top: `${pctOf(r)}%` };
              const inner = cell !== 'Empty' ? (
                <JungoStone key={cell} firstPaint={firstPaint}>
                  {isPa && <span className="pa-mark" aria-hidden />}
                </JungoStone>
              ) : (
                isPendingTap && <span className="pending-stone" aria-hidden />
              );
              // mini モードは親 (BoardThumb の tab) が <button> なので、 入れ子の button を
              // 避けるため div を使う。 通常モードは button のままで a11y を保つ。
              if (mini) {
                return (
                  <div key={`${r}-${c}`} className={cls} style={style} aria-hidden>
                    {inner}
                  </div>
                );
              }
              return (
                <button
                  key={`${r}-${c}`}
                  className={cls}
                  style={style}
                  onClick={handler}
                  disabled={!isLegal}
                  aria-label={`row ${r} col ${c}`}
                >
                  {inner}
                </button>
              );
            }),
          )}
        </div>
      </div>
      {!mini && hasPass && (
        <button
          className="jungo-pass-btn"
          onClick={() => onMove?.('Pass')}
          data-testid="jungo-pass"
          disabled={!isMyTurn}
        >
          パス
        </button>
      )}
      {showOppHint && (
        <div className="board-turn-hint" aria-hidden>
          手番待ち
        </div>
      )}
    </div>
  );
}

/**
 * 石 1 個。 `no-anim` は **生成時点の値で固定** する。
 * render のたびに firstPaint を読み直すと、 仮置きなどの再描画で
 * 全部の石から no-anim が外れて一斉にアニメしてしまう。
 */
function JungoStone({
  firstPaint,
  children,
}: {
  firstPaint: RefObject<boolean>;
  children?: ReactNode;
}) {
  const noAnim = useRef(firstPaint.current).current;
  return <span className={`stone${noAnim ? ' no-anim' : ''}`}>{children}</span>;
}
